import { useState, useEffect, useRef } from 'react';
import ClickRipple from '../ClickRipple';
import { analyseClient } from '../../services/api/apiClient';
import { ApiResponse } from '@ocktra/shared-types';

interface Message {
    id: string;
    type: 'user' | 'ai' | 'command' | 'system';
    content: string;
    timestamp: Date;
    isVoice?: boolean;
    commandData?: {
        action: string;
        status: 'pending' | 'success' | 'error';
        result?: any;
    };
}

interface VoiceRecording {
    blob: Blob | null;
    duration: number;
    isRecording: boolean;
}

const Chat = () => {
    const [messages, setMessages] = useState<Message[]>([
        {
            id: '1',
            type: 'ai',
            content: 'Hello! I\'m your AI trading assistant. How can I help you today?',
            timestamp: new Date(),
        },
    ]);
    const [inputText, setInputText] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [voiceMode, setVoiceMode] = useState(false);
    const [voiceRecording, setVoiceRecording] = useState<VoiceRecording>({
        blob: null,
        duration: 0,
        isRecording: false,
    });
    const [isSending, setIsSending] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const sendMessage = async (text?: string, voiceBlob?: Blob) => {
        const content = text || inputText.trim();
        if (!content && !voiceBlob) return;

        setIsSending(true);

        // Add user message
        const userMessage: Message = {
            id: Date.now().toString(),
            type: 'user',
            content: voiceBlob ? '🎤 Voice message' : content,
            timestamp: new Date(),
            isVoice: !!voiceBlob,
        };

        setMessages((prev) => [...prev, userMessage]);
        setInputText('');

        // Show typing indicator
        setIsTyping(true);

        try {
            let aiResponse: Message;

            if (voiceBlob) {
                // Send voice message
                const formData = new FormData();
                formData.append('voice', voiceBlob, 'voice-message.webm');

                const response = await analyseClient.post<{ success: boolean; data: { message: string } }>('/chat/voice', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                aiResponse = {
                    id: (Date.now() + 1).toString(),
                    type: 'ai',
                    content: response.data?.message || "I've received your voice message. Processing it now...",
                    timestamp: new Date(),
                };
            } else {
                // Send text message
                const response = await analyseClient.post< ApiResponse<string>>('/ai/chat', {
                    message: content,
                });

                aiResponse = {
                    id: (Date.now() + 1).toString(),
                    type: 'ai',
                    content: response.data || 'I understand. Let me help you with that.',
                    timestamp: new Date(),
                };
            }

            setMessages((prev) => [...prev, aiResponse]);
            setIsTyping(false);
            setIsSending(false);
        } catch (error) {
            console.error('[Chat] Error sending message:', error);

            // Show error message to user
            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                type: 'system',
                content: 'Sorry, I encountered an error processing your message. Please try again.',
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMessage]);

            setIsTyping(false);
            setIsSending(false);
        }
    };

    const startVoiceRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.addEventListener('dataavailable', (event) => {
                audioChunksRef.current.push(event.data);
            });

            mediaRecorder.addEventListener('stop', () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                setVoiceRecording((prev) => ({ ...prev, blob: audioBlob, isRecording: false }));
                stream.getTracks().forEach((track) => track.stop());
            });

            mediaRecorder.start();
            setVoiceRecording({ blob: null, duration: 0, isRecording: true });

            // Start duration timer
            recordingTimerRef.current = setInterval(() => {
                setVoiceRecording((prev) => ({ ...prev, duration: prev.duration + 1 }));
            }, 1000);
        } catch (error) {
            console.error('[Chat] Error starting voice recording:', error);
            alert('Failed to access microphone. Please grant permission.');
        }
    };

    const stopVoiceRecording = () => {
        if (mediaRecorderRef.current && voiceRecording.isRecording) {
            mediaRecorderRef.current.stop();
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
        }
    };

    const sendVoiceMessage = () => {
        if (voiceRecording.blob) {
            sendMessage(undefined, voiceRecording.blob);
            setVoiceRecording({ blob: null, duration: 0, isRecording: false });
        }
    };

    const cancelVoiceRecording = () => {
        stopVoiceRecording();
        setVoiceRecording({ blob: null, duration: 0, isRecording: false });
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getMessageIcon = (type: Message['type']) => {
        switch (type) {
            case 'ai':
                return (
                    <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                        style={{ background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-700))' }}
                    >
                        AI
                    </div>
                );
            case 'user':
                return (
                    <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                        style={{ background: 'linear-gradient(135deg, var(--color-secondary-500), var(--color-secondary-700))' }}
                    >
                        U
                    </div>
                );
            case 'command':
                return (
                    <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white"
                        style={{ backgroundColor: 'var(--color-warning-500)' }}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                    </div>
                );
            case 'system':
                return (
                    <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white"
                        style={{ backgroundColor: 'var(--color-gray-500)' }}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                );
        }
    };

    return (
        <div className="flex flex-col h-full bg-surface" style={{ backgroundColor: 'var(--color-surface)' }}>
            {/* Header */}
            <div
                className="px-4 py-3 border-b flex items-center justify-between"
                style={{
                    borderColor: 'var(--color-border-light)',
                    backgroundColor: 'var(--color-surface-elevated)',
                }}
            >
                <div className="flex items-center gap-3">
                    <div
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-700))' }}
                    >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                            AI Assistant
                        </h3>
                        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                            {isTyping ? 'Typing...' : 'Online'}
                        </p>
                    </div>
                </div>

                <ClickRipple>
                    <button
                        onClick={() => setVoiceMode(!voiceMode)}
                        className="p-2 rounded-lg hover:bg-opacity-80 transition-all relative"
                        style={{
                            backgroundColor: voiceMode ? 'var(--color-primary-100)' : 'transparent',
                            color: voiceMode ? 'var(--color-primary-700)' : 'var(--color-text-secondary)',
                        }}
                        title="Toggle voice mode"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        </svg>
                    </button>
                </ClickRipple>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                {messages.map((message) => (
                    <div
                        key={message.id}
                        className={`flex gap-3 ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                    >
                        {getMessageIcon(message.type)}

                        <div
                            className={`flex-1 max-w-[75%] ${message.type === 'user' ? 'items-end' : 'items-start'} flex flex-col gap-1`}
                        >
                            <div
                                className="px-4 py-3 rounded-lg shadow-sm"
                                style={{
                                    backgroundColor:
                                        message.type === 'user'
                                            ? 'var(--color-primary-500)'
                                            : message.type === 'command'
                                                ? 'var(--color-warning-50)'
                                                : 'var(--color-background-secondary)',
                                    color:
                                        message.type === 'user'
                                            ? 'white'
                                            : message.type === 'command'
                                                ? 'var(--color-warning-900)'
                                                : 'var(--color-text-primary)',
                                    border: message.type === 'command' ? '1px solid var(--color-warning-300)' : 'none',
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                }}
                            >
                                {message.isVoice && (
                                    <div className="flex items-center gap-2 mb-1">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                )}
                                <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>

                                {message.commandData && (
                                    <div className="mt-2 pt-2 border-t" style={{ borderColor: 'var(--color-warning-200)' }}>
                                        <div className="flex items-center gap-2 text-xs">
                                            <span className="font-medium">Status:</span>
                                            <span
                                                className="px-2 py-0.5 rounded-full"
                                                style={{
                                                    backgroundColor:
                                                        message.commandData.status === 'success'
                                                            ? 'var(--color-success-100)'
                                                            : message.commandData.status === 'error'
                                                                ? 'var(--color-danger-100)'
                                                                : 'var(--color-gray-100)',
                                                    color:
                                                        message.commandData.status === 'success'
                                                            ? 'var(--color-success-800)'
                                                            : message.commandData.status === 'error'
                                                                ? 'var(--color-danger-800)'
                                                                : 'var(--color-gray-800)',
                                                }}
                                            >
                                                {message.commandData.status}
                                            </span>
                                        </div>
                                        {message.commandData.result && (
                                            <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                                                {message.commandData.result}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <span className="text-xs px-2" style={{ color: 'var(--color-text-tertiary)' }}>
                                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        </div>
                    </div>
                ))}

                {isTyping && (
                    <div className="flex gap-3">
                        {getMessageIcon('ai')}
                        <div
                            className="px-4 py-3 rounded-lg shadow-sm"
                            style={{ backgroundColor: 'var(--color-background-secondary)' }}
                        >
                            <div className="flex gap-1">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-gray-400)', animation: 'bounce 1s infinite' }} />
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-gray-400)', animation: 'bounce 1s infinite 0.15s' }} />
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-gray-400)', animation: 'bounce 1s infinite 0.3s' }} />
                            </div>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Voice Recording UI */}
            {voiceRecording.isRecording && (
                <div
                    className="px-4 py-3 border-t"
                    style={{
                        borderColor: 'var(--color-border-light)',
                        backgroundColor: 'var(--color-danger-50)',
                    }}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div
                                className="w-3 h-3 rounded-full"
                                style={{
                                    backgroundColor: 'var(--color-danger-500)',
                                    animation: 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                                }}
                            />
                            <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                                Recording... {formatTime(voiceRecording.duration)}
                            </span>
                        </div>
                        <div className="flex gap-2">
                            <ClickRipple>
                                <button
                                    onClick={cancelVoiceRecording}
                                    className="px-6 py-2.5 rounded-lg text-sm font-medium transition-all relative"
                                    style={{
                                        backgroundColor: 'var(--color-gray-200)',
                                        color: 'var(--color-text-primary)',
                                    }}
                                >
                                    Cancel
                                </button>
                            </ClickRipple>
                            <ClickRipple>
                                <button
                                    onClick={stopVoiceRecording}
                                    className="px-6 py-2.5 rounded-lg text-sm font-medium transition-all relative"
                                    style={{
                                        backgroundColor: 'var(--color-danger-500)',
                                        color: 'white',
                                    }}
                                >
                                    Stop
                                </button>
                            </ClickRipple>
                        </div>
                    </div>
                </div>
            )}

            {/* Voice Preview UI */}
            {voiceRecording.blob && !voiceRecording.isRecording && (
                <div
                    className="px-4 py-3 border-t"
                    style={{
                        borderColor: 'var(--color-border-light)',
                        backgroundColor: 'var(--color-secondary-50)',
                    }}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <svg className="w-5 h-5" style={{ color: 'var(--color-secondary-600)' }} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                                Voice message ready ({formatTime(voiceRecording.duration)})
                            </span>
                        </div>
                        <div className="flex gap-2">
                            <ClickRipple>
                                <button
                                    onClick={cancelVoiceRecording}
                                    className="px-6 py-2.5 rounded-lg text-sm font-medium transition-all relative"
                                    style={{
                                        backgroundColor: 'var(--color-gray-200)',
                                        color: 'var(--color-text-primary)',
                                    }}
                                >
                                    Discard
                                </button>
                            </ClickRipple>
                            <ClickRipple>
                                <button
                                    onClick={sendVoiceMessage}
                                    disabled={isSending}
                                    className="px-6 py-2.5 rounded-lg text-sm font-medium transition-all relative disabled:opacity-50"
                                    style={{
                                        backgroundColor: 'var(--color-secondary-500)',
                                        color: 'white',
                                    }}
                                >
                                    {isSending ? 'Sending...' : 'Send'}
                                </button>
                            </ClickRipple>
                        </div>
                    </div>
                </div>
            )}

            {/* Input Area */}
            {!voiceRecording.isRecording && !voiceRecording.blob && (
                <div
                    className="px-4 py-3 border-t"
                    style={{ borderColor: 'var(--color-border-light)', backgroundColor: 'var(--color-surface-elevated)' }}
                >
                    <div className="flex items-end gap-2">
                        {voiceMode ? (
                            <ClickRipple>
                                <button
                                    onClick={startVoiceRecording}
                                    className="flex-1 px-6 py-3.5 rounded-lg font-medium transition-all flex items-center justify-center gap-2 relative"
                                    style={{
                                        backgroundColor: 'var(--color-danger-500)',
                                        color: 'white',
                                    }}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                    </svg>
                                    Hold to Record
                                </button>
                            </ClickRipple>
                        ) : (
                            <>
                                <textarea
                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            sendMessage();
                                        }
                                    }}
                                    placeholder="Type your message..."
                                    className="flex-1 px-4 py-3 rounded-sm resize-none focus:outline-none focus:ring-2 transition-all"
                                    style={{
                                        backgroundColor: 'var(--color-background-secondary)',
                                        color: 'var(--color-text-primary)',
                                        border: '1px solid var(--color-border-light)',
                                    }}
                                    rows={1}
                                    maxLength={1000}
                                />
                                <ClickRipple>
                                    <button
                                        onClick={() => sendMessage()}
                                        disabled={!inputText.trim() || isSending}
                                        className="p-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed relative"
                                        style={{
                                            backgroundColor: inputText.trim() ? 'var(--color-primary-500)' : 'var(--color-gray-300)',
                                            color: 'white',
                                        }}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                        </svg>
                                    </button>
                                </ClickRipple>
                            </>
                        )}
                    </div>

                    {!voiceMode && (
                        <p className="text-xs mt-2 text-center" style={{ color: 'var(--color-text-tertiary)' }}>
                            Press Enter to send • Shift+Enter for new line
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default Chat;
